/**
 * Lectura de las analíticas.
 *
 * Va **directo a PostgREST**, y eso es correcto aquí aunque sean tablas de partida: el principio VI
 * prohíbe **escribir** desde el navegador, no leer. Y quién puede leer qué lo decide la base de
 * datos: `answers` y `game_questions` solo las lee quien alojó la partida (o quien administra), así
 * que este código no puede filtrar nada que las políticas no permitan.
 *
 * El cuestionario **congelado** es la fuente de los enunciados y de las opciones, no las tablas de
 * contenido: la pregunta puede haberse editado o borrado desde entonces, y las analíticas de una
 * partida jugada no cambian por eso (§6.2).
 */

import type {
  AnalyticsParticipant,
  GameReport,
  PlayedQuestion,
  QuizSnapshot,
  StoredAnswer,
} from '@clica/shared';
import { buildReport } from '@clica/shared';
import { supabase } from '../auth/supabase.js';
import { fetchGameSnapshot } from '../api/client.js';

/** Acota en un solo sitio la conversión: sin tipos generados, `supabase-js` devuelve `any`. */
function rows<T>(data: unknown): readonly T[] {
  return (data ?? []) as readonly T[];
}

export interface GameSummary {
  readonly id: string;
  readonly pin: string;
  readonly status: string;
  readonly created_at: string;
  readonly ended_at: string | null;
  readonly quiz_id: string;
}

/** Las partidas de un cuestionario, de la más reciente a la más antigua. */
export async function listGames(quizId: string): Promise<readonly GameSummary[]> {
  const { data, error } = await supabase
    .from('games')
    .select('id, pin, status, created_at, ended_at, quiz_id')
    .eq('quiz_id', quizId)
    .order('created_at', { ascending: false });

  if (error !== null) throw error;
  return rows<GameSummary>(data);
}

interface GameRow {
  readonly id: string;
  readonly quiz_snapshot: QuizSnapshot;
  readonly created_at: string;
  readonly ended_at: string | null;
  readonly participants: readonly {
    readonly id: string;
    readonly display_name: string;
    readonly alias: string | null;
    readonly connection_state: string;
    readonly score: number;
  }[];
  readonly game_questions: readonly {
    readonly question_id: string;
    readonly position: number;
    readonly closed_at: string | null;
  }[];
}

export interface GameAnalytics {
  readonly gameId: string;
  readonly title: string;
  readonly createdAt: string;
  readonly endedAt: string | null;
  readonly report: GameReport;
}

/**
 * El informe de una partida.
 *
 * Las respuestas se piden aparte porque son la tabla grande —cuarenta personas por diez preguntas—
 * y anidarlas en la consulta de la partida traería la misma fila de participante cuarenta veces.
 */
export async function fetchAnalytics(gameId: string): Promise<GameAnalytics> {
  /*
   * **El cuestionario congelado se pide al servidor, el resto a PostgREST.**
   *
   * `games.quiz_snapshot` está revocado para el rol `authenticated` porque dentro están las
   * respuestas correctas, y ese rol lo tiene todo el mundo con sesión: la alumna y la profesora
   * por igual. Lo que distingue a la profesora es RLS, que decide filas y no columnas. Pedirlo
   * aquí hacía que **el informe no cargara para nadie**, ni para quien había alojado la partida.
   *
   * Las dos peticiones van en paralelo: son independientes y una espera detrás de otra sería
   * medio segundo de pantalla en blanco por nada.
   */
  const [{ data, error }, snapshot] = await Promise.all([
    supabase
      .from('games')
      .select(
        `id, created_at, ended_at,
         participants ( id, display_name, alias, connection_state, score ),
         game_questions ( question_id, position, closed_at )`,
      )
      .eq('id', gameId)
      .maybeSingle(),
    fetchGameSnapshot(gameId),
  ]);

  if (error !== null) throw error;
  if (data === null) throw new Error('la partida no existe o no es tuya');

  const game = {
    ...(data as unknown as Omit<GameRow, 'quiz_snapshot'>),
    quiz_snapshot: snapshot as QuizSnapshot,
  };

  const { data: answerData, error: answerError } = await supabase
    .from('answers')
    .select('participant_id, question_id, option_id, is_correct, points, elapsed_ms')
    .eq('game_id', gameId);

  if (answerError !== null) throw answerError;

  const answers: readonly StoredAnswer[] = rows<{
    participant_id: string;
    question_id: string;
    option_id: string | null;
    is_correct: boolean;
    points: number;
    elapsed_ms: number;
  }>(answerData).map((answer) => ({
    participantId: answer.participant_id,
    questionId: answer.question_id,
    optionId: answer.option_id,
    isCorrect: answer.is_correct,
    points: answer.points,
    elapsedMs: answer.elapsed_ms,
  }));

  // Solo las preguntas que se llegaron a jugar: las del cuestionario que no dio tiempo a abrir no
  // son «mal resueltas», simplemente no se jugaron.
  const jugadas = new Map(game.game_questions.map((q) => [q.question_id, q]));

  const questions: readonly PlayedQuestion[] = game.quiz_snapshot.questions
    .filter((question) => jugadas.has(question.id))
    .map((question) => ({
      questionId: question.id,
      position: jugadas.get(question.id)?.position ?? question.position,
      promptMd: question.prompt_md,
      options: question.options.map((option) => ({
        id: option.id,
        textMd: option.text_md,
        correct: option.is_correct,
      })),
      closedAt: jugadas.get(question.id)?.closed_at ?? null,
    }));

  const participants: readonly AnalyticsParticipant[] = game.participants.map((participant) => ({
    participantId: participant.id,
    displayName: participant.display_name,
    alias: participant.alias,
    connection: participant.connection_state as 'online' | 'offline' | 'kicked',
    score: participant.score,
  }));

  return {
    gameId: game.id,
    title: game.quiz_snapshot.title,
    createdAt: game.created_at,
    endedAt: game.ended_at,
    report: buildReport(questions, participants, answers),
  };
}
